import React, { useState, useEffect } from 'react';
import { Wrench, Plus, Search, Edit2, Trash2, X, Clock } from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { useToast } from '../../../components/ui/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UNITS = [
  { value: 'hour', label: 'Hour' },
  { value: 'day', label: 'Day' },
  { value: 'project', label: 'Project' },
  { value: 'package', label: 'Package' },
  { value: 'consultation', label: 'Consultation' },
  { value: 'other', label: 'Other' }
];

const FinancialServices = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const { toast } = useToast();
  const token = localStorage.getItem('admin_token');

  const [formData, setFormData] = useState({
    name: '', unit: 'hour', price: 0, currency: 'AED', tax_rate: 5.0, description: '', active: true
  });

  useEffect(() => { fetchServices(); }, [search]);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      const response = await fetch(`${API_URL}/api/financial/services?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed');
      setServices(await response.json());
    } catch (error) {
      toast({ variant: 'destructive', title: 'Error', description: 'Failed to load services' });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', unit: 'hour', price: 0, currency: 'AED', tax_rate: 5.0, description: '', active: true });
    setEditingService(null);
  };

  const openCreate = () => { resetForm(); setShowModal(true); };

  const openEdit = (service) => {
    setEditingService(service);
    setFormData({
      name: service.name, unit: service.unit, price: service.price,
      currency: service.currency, tax_rate: service.tax_rate,
      description: service.description || '', active: service.active
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingService ? `${API_URL}/api/financial/services/${editingService.id}` : `${API_URL}/api/financial/services`;
      const method = editingService ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method, headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ ...formData, price: parseFloat(formData.price), tax_rate: parseFloat(formData.tax_rate) })
      });
      if (!response.ok) throw new Error('Failed');
      toast({ title: 'Success', description: `Service ${editingService ? 'updated' : 'created'}` });
      setShowModal(false);
      fetchServices();
    } catch (error) {
      toast({ variant: 'destructive', title: 'Error', description: error.message });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this service?')) return;
    try {
      await fetch(`${API_URL}/api/financial/services/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
      toast({ title: 'Success', description: 'Service deleted' });
      fetchServices();
    } catch (error) {
      toast({ variant: 'destructive', title: 'Error', description: 'Failed to delete' });
    }
  };

  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'AED' }).format(amount);
  };

  return (
    <div className="p-6 space-y-6" data-testid="financial-services-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#2B2D42]">Services</h1>
          <p className="text-sm text-[#6C757D]">Finance &gt; Master Data &gt; Services</p>
        </div>
        <Button onClick={openCreate} className="bg-[#FF6B35] hover:bg-[#E5562D]" data-testid="create-service-btn">
          <Plus className="w-4 h-4 mr-2" /> New Service
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#6C757D]" />
        <Input placeholder="Search services..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="service-search-input" />
      </div>

      {loading ? (
        <div className="flex justify-center py-12 text-[#6C757D]">Loading services...</div>
      ) : services.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-[#6C757D]">No services found.</CardContent></Card>
      ) : (
        <div className="grid gap-4">
          {services.map((service) => (
            <Card key={service.id} className="hover:shadow-md transition-shadow" data-testid={`service-card-${service.id}`}>
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-[#023E8A] bg-opacity-10 rounded-full flex items-center justify-center">
                      <Wrench className="w-5 h-5 text-[#023E8A]" />
                    </div>
                    <div>
                      <div className="font-semibold text-[#2B2D42]">{service.name}</div>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="text-xs flex items-center gap-1">
                          <Clock className="w-3 h-3" /> per {service.unit}
                        </Badge>
                        <Badge className={`text-xs ${service.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                          {service.active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <div className="font-bold text-lg text-[#FF6B35]">{formatCurrency(service.price, service.currency)}</div>
                      <div className="text-xs text-[#6C757D]">Tax: {service.tax_rate}%</div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(service)} data-testid={`edit-service-${service.id}`}><Edit2 className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(service.id)} data-testid={`delete-service-${service.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-[#2B2D42]">{editingService ? 'Edit Service' : 'New Service'}</h3>
              <button onClick={() => setShowModal(false)}><X className="h-5 w-5 text-gray-500" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#2B2D42] mb-1">Name *</label>
                  <Input required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} data-testid="service-name-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2B2D42] mb-1">Unit</label>
                  <select value={formData.unit} onChange={(e) => setFormData({...formData, unit: e.target.value})}
                    className="w-full px-3 py-2 border rounded-md text-sm" data-testid="service-unit-input">
                    {UNITS.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2B2D42] mb-1">Price *</label>
                  <Input required type="number" step="0.01" min="0" value={formData.price} onChange={(e) => setFormData({...formData, price: e.target.value})} data-testid="service-price-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2B2D42] mb-1">Currency</label>
                  <select value={formData.currency} onChange={(e) => setFormData({...formData, currency: e.target.value})}
                    className="w-full px-3 py-2 border rounded-md text-sm" data-testid="service-currency-input">
                    <option value="AED">AED</option><option value="USD">USD</option><option value="EUR">EUR</option><option value="GBP">GBP</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2B2D42] mb-1">Tax Rate (%)</label>
                  <Input type="number" step="0.1" min="0" max="100" value={formData.tax_rate} onChange={(e) => setFormData({...formData, tax_rate: e.target.value})} data-testid="service-tax-input" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2B2D42] mb-1">Description</label>
                <textarea className="w-full px-3 py-2 border rounded-md text-sm" rows="2" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={formData.active} onChange={(e) => setFormData({...formData, active: e.target.checked})} id="service-active" />
                <label htmlFor="service-active" className="text-sm text-[#2B2D42]">Active</label>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
                <Button type="submit" className="bg-[#FF6B35] hover:bg-[#E5562D]" data-testid="save-service-btn">{editingService ? 'Update' : 'Create'} Service</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialServices;
